import {
  CompanyType,
  EstimatedProjectValueBand,
  PlanningApplicationStatus,
  PlanningApplicationType,
  PlanningCategory,
  PlanningClassification,
  allEstimatedProjectValueBands,
  allPlanningApplicationStatuses,
  allPlanningApplicationTypes,
} from '@leadgen/models/client'
import {
  getPlanningCategoriesForClassifications,
  planningCategoriesByCompanyTypeMap,
  planningCategoryMap,
  planningClassificationMap,
  planningClassificationsByCompanyTypeMap,
} from '../../constants/planningTaxonomy'

export type FilterSummarySection = {
  label: string
  text: string
}

const companyTypeTitleMap: Record<CompanyType, string> = {
  builder: 'Building',
  glazier: 'Glazing',
  landscaper: 'Landscaping',
  renewables: 'Renewables',
  flooring: 'Flooring',
  kitchen: 'Kitchens',
  retail: 'Retail',
}

const planningApplicationTypeTitleMap: Record<PlanningApplicationType, string> = {
  listed: 'Listed',
  unlisted: 'Unlisted',
}

const planningApplicationStatusTitleMap: Record<PlanningApplicationStatus, string> = {
  submitted: 'Submitted',
  approved: 'Approved',
}

const estimatedProjectValueBandTitleMap: Record<EstimatedProjectValueBand, string> = {
  'under-10k': '£0-£10k',
  '10k-25k': '£10k-£25k',
  '25k-50k': '£25k-£50k',
  '50k-100k': '£50k-£100k',
  '100k-250k': '£100k-£250k',
  '250k-500k': '£250k-£500k',
  '500k-1m': '£500k-£1m',
  '1m-plus': '£1m+',
}

const estimatedProjectValueBandBoundMap: Record<
  EstimatedProjectValueBand,
  { lower: string; upper: string | null }
> = {
  'under-10k': { lower: '£0', upper: '£10k' },
  '10k-25k': { lower: '£10k', upper: '£25k' },
  '25k-50k': { lower: '£25k', upper: '£50k' },
  '50k-100k': { lower: '£50k', upper: '£100k' },
  '100k-250k': { lower: '£100k', upper: '£250k' },
  '250k-500k': { lower: '£250k', upper: '£500k' },
  '500k-1m': { lower: '£500k', upper: '£1m' },
  '1m-plus': { lower: '£1m', upper: null },
}

const formatHumanList = (values: string[]): string => {
  if (!values.length) {
    return ''
  }
  if (values.length === 1) {
    return values[0]
  }
  if (values.length === 2) {
    return `${values[0]} and ${values[1]}`
  }
  return `${values.slice(0, -1).join(', ')}, and ${values[values.length - 1]}`
}

const orderSelectedValues = <T extends string>(
  selectedValues: readonly T[],
  orderedValues: readonly T[],
): T[] => {
  const selectedValueSet = new Set(selectedValues)
  return [
    ...orderedValues.filter(value => selectedValueSet.has(value)),
    ...selectedValues.filter(value => !orderedValues.includes(value)),
  ]
}

const containsSameValues = <T extends string>(
  selectedValues: readonly T[],
  availableValues: readonly T[],
): boolean => {
  const selectedValueSet = new Set(selectedValues)
  return (
    selectedValues.length === availableValues.length &&
    availableValues.every(value => selectedValueSet.has(value))
  )
}

const formatEstimatedProjectValueBandRun = (
  estimatedProjectValueBands: EstimatedProjectValueBand[],
): string => {
  if (estimatedProjectValueBands.length === 1) {
    return estimatedProjectValueBandTitleMap[estimatedProjectValueBands[0]]
  }

  const firstBand = estimatedProjectValueBandBoundMap[estimatedProjectValueBands[0]]
  const lastBand =
    estimatedProjectValueBandBoundMap[
      estimatedProjectValueBands[estimatedProjectValueBands.length - 1]
    ]

  return lastBand.upper ? `${firstBand.lower}-${lastBand.upper}` : `${firstBand.lower}+`
}

const formatEstimatedProjectValueBandRuns = (
  estimatedProjectValueBands: readonly EstimatedProjectValueBand[],
): string => {
  const orderedEstimatedProjectValueBands = orderSelectedValues(
    estimatedProjectValueBands,
    allEstimatedProjectValueBands,
  )
  const selectedEstimatedProjectValueBandSet = new Set(orderedEstimatedProjectValueBands)
  const runs = allEstimatedProjectValueBands.reduce<EstimatedProjectValueBand[][]>(
    (memo, estimatedProjectValueBand, index) => {
      if (!selectedEstimatedProjectValueBandSet.has(estimatedProjectValueBand)) {
        return memo
      }
      const previousEstimatedProjectValueBand = allEstimatedProjectValueBands[index - 1]
      const isSequential =
        !!memo.length &&
        previousEstimatedProjectValueBand &&
        selectedEstimatedProjectValueBandSet.has(previousEstimatedProjectValueBand)

      if (!isSequential) {
        memo.push([estimatedProjectValueBand])
        return memo
      }

      memo[memo.length - 1].push(estimatedProjectValueBand)
      return memo
    },
    [],
  )

  return formatHumanList(runs.map(formatEstimatedProjectValueBandRun))
}

class FilterSummaryFormatter {
  formatSelectedFilters = ({
    companyType,
    planningApplicationTypes,
    planningApplicationStatuses,
    planningClassifications,
    planningCategories,
    estimatedProjectValueBands,
  }: {
    companyType: CompanyType | null
    planningApplicationTypes: readonly PlanningApplicationType[]
    planningApplicationStatuses: readonly PlanningApplicationStatus[]
    planningClassifications: readonly PlanningClassification[]
    planningCategories: readonly PlanningCategory[]
    estimatedProjectValueBands: readonly EstimatedProjectValueBand[]
  }): FilterSummarySection[] => [
    {
      label: 'Business type',
      text: companyType ? companyTypeTitleMap[companyType] : 'Not selected',
    },
    {
      label: 'Application types',
      text: this.formatPlanningApplicationTypes(planningApplicationTypes),
    },
    {
      label: 'Application statuses',
      text: this.formatPlanningApplicationStatuses(planningApplicationStatuses),
    },
    {
      label: 'Applications about',
      text: this.formatPlanningClassifications(companyType, planningClassifications),
    },
    {
      label: 'Work categories',
      text: this.formatPlanningCategories(companyType, planningClassifications, planningCategories),
    },
    {
      label: 'Project values',
      text: this.formatEstimatedProjectValueBands(estimatedProjectValueBands),
    },
  ]

  private formatPlanningApplicationTypes = (
    planningApplicationTypes: readonly PlanningApplicationType[],
  ): string => {
    if (!planningApplicationTypes.length) {
      return 'No application types selected'
    }
    return formatHumanList(
      orderSelectedValues(planningApplicationTypes, allPlanningApplicationTypes).map(
        planningApplicationType => planningApplicationTypeTitleMap[planningApplicationType],
      ),
    )
  }

  private formatPlanningApplicationStatuses = (
    planningApplicationStatuses: readonly PlanningApplicationStatus[],
  ): string => {
    if (!planningApplicationStatuses.length) {
      return 'No application statuses selected'
    }
    return formatHumanList(
      orderSelectedValues(planningApplicationStatuses, allPlanningApplicationStatuses).map(
        planningApplicationStatus => planningApplicationStatusTitleMap[planningApplicationStatus],
      ),
    )
  }

  private formatPlanningClassifications = (
    companyType: CompanyType | null,
    planningClassifications: readonly PlanningClassification[],
  ): string => {
    if (!planningClassifications.length) {
      return 'No application classifications selected'
    }

    const availablePlanningClassifications = companyType
      ? planningClassificationsByCompanyTypeMap[companyType]
      : []
    if (containsSameValues(planningClassifications, availablePlanningClassifications)) {
      return 'All application classifications'
    }

    return formatHumanList(
      orderSelectedValues(planningClassifications, availablePlanningClassifications).map(
        planningClassification => planningClassificationMap[planningClassification].title,
      ),
    )
  }

  private formatPlanningCategories = (
    companyType: CompanyType | null,
    planningClassifications: readonly PlanningClassification[],
    planningCategories: readonly PlanningCategory[],
  ): string => {
    if (!planningCategories.length) {
      return 'No work categories selected'
    }

    const availablePlanningCategories = companyType
      ? getPlanningCategoriesForClassifications(planningClassifications).filter(planningCategory =>
          planningCategoriesByCompanyTypeMap[companyType].includes(planningCategory),
        )
      : []
    if (containsSameValues(planningCategories, availablePlanningCategories)) {
      return 'All available work categories'
    }

    return formatHumanList(
      orderSelectedValues(planningCategories, availablePlanningCategories).map(
        planningCategory => planningCategoryMap[planningCategory].title,
      ),
    )
  }

  private formatEstimatedProjectValueBands = (
    estimatedProjectValueBands: readonly EstimatedProjectValueBand[],
  ): string => {
    if (!estimatedProjectValueBands.length) {
      return 'No project value bands selected'
    }

    if (containsSameValues(estimatedProjectValueBands, allEstimatedProjectValueBands)) {
      return 'All project value bands'
    }

    return formatEstimatedProjectValueBandRuns(estimatedProjectValueBands)
  }
}

export default new FilterSummaryFormatter()
